import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Loader, Modal } from 'semantic-ui-react';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import AddCardForm from '../../components/AddCardForm';
import sendToast from '../../../utils/Toast';
import {
    createMembershipSubscription,
    cancelMembershipSubscription,
    getMembershipSubscription,
    updateMembershipSubscription,
} from '../../services/Payments';
import { MembershipSubscription } from '../../types/Payments';
import { clearGreetingsSummaryCache, isGreetingsAddonSubscription } from '../../hooks/useGreetingsSummary';
import { approveMembershipPayment } from '../../utils/approveMembershipPayment';

// Enh 4 -- Daily Greetings paid add-on (planKey: greetingsAddon). Reuses the
// same MembershipPlans/MembershipSubscriptions collections and card-capture
// flow as the guest membership panel on the Payments page, just scoped to a
// single resident and a different plan.
//
// Controlled component: all subscription/fee/card-on-file data is fetched by
// the parent page (neighbors-greetings/index.tsx, alongside its other
// resident data) and passed in as props; this component only drives the
// subscribe/cancel/resume mutations and reports the result back via onChange.
interface GreetingsAddonCardProps {
    // 'enroll': not yet subscribed -- larger card with fee + Subscribe CTA.
    // 'manage': already subscribed -- rendered in two separate places on the
    // page (see manageSection), not as one combined row.
    mode: 'enroll' | 'manage';
    // Card spec, Enh 4: the cancel control lives in its own section at the
    // bottom of the page, separate from the billing status line near the top.
    // Required when mode === 'manage'.
    manageSection?: 'status' | 'action';
    residentId: string;
    facilityId: string;
    subscription: MembershipSubscription | null;
    feeCents: number | null;
    currency: string;
    interval: string;
    hasDefaultPaymentMethod: boolean;
    stripePromise: Promise<Stripe | null> | null;
    onChange: (subscription: MembershipSubscription | null) => void;
    // 'enroll' mode only: bump to open the sign-up flow from outside (the Hello! card's
    // "Turn on" pill), exactly as if "Sign Up for Greetings" had been pressed.
    signupRequest?: number;
    // 'enroll' mode only: called right after a card is saved for this sign-up, before the subscribe
    // call, so the page remembers the card even if the subscribe then fails.
    onCardSaved?: () => void;
    // 'enroll' mode only: the saved-card lookup failed, so whether a card exists is unknown. Sign Up
    // asks to try again instead of opening Add a card (which could add a second card).
    cardsLookupFailed?: boolean;
    // Reloads the fee, cards and subscription (the "Try again" buttons below).
    onRetryLookup?: () => void;
}

const formatDate = (ms: number | null | undefined) => {
    if (!ms) return null;
    return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatFee = (feeCents: number | null, currency: string, interval: string) => {
    if (feeCents === null || !Number.isFinite(feeCents) || feeCents <= 0) return '';
    const dollars = feeCents / 100;
    const amount = dollars.toLocaleString(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2,
    });
    return `${amount}/${interval || 'month'}`;
};

// Statuses where Stripe couldn't collect payment -- greetings are still
// sending on a grace period, but the resident needs to act before they stop.
// These are the live statuses in useGreetingsSummary.ts minus the healthy one (active).
const PAYMENT_ISSUE_STATUSES = ['past_due', 'payment_action_required', 'unpaid'];

const GreetingsAddonCard: React.FC<GreetingsAddonCardProps> = ({
    mode,
    manageSection,
    residentId,
    facilityId,
    subscription,
    feeCents,
    currency,
    interval,
    hasDefaultPaymentMethod,
    stripePromise,
    onChange,
    signupRequest = 0,
    onCardSaved,
    cardsLookupFailed = false,
    onRetryLookup,
}) => {
    const [actionLoading, setActionLoading] = useState<'' | 'subscribe' | 'cancel' | 'resume'>('');
    // True while the bank's approval popup is open for the first payment.
    const [awaitingBank, setAwaitingBank] = useState<boolean>(false);
    // Blocks a second subscribe (double click, or a card save landing on top of a confirm) before the
    // button has re-rendered.
    const subscribeInFlightRef = useRef<boolean>(false);
    const [addCardOpen, setAddCardOpen] = useState<boolean>(false);
    // True while the add-card form is saving the card: closing the window then would unmount the card
    // field mid-confirm and show an error for a card that was saved.
    const [savingCard, setSavingCard] = useState<boolean>(false);
    const [confirmSubscribeOpen, setConfirmSubscribeOpen] = useState<boolean>(false);
    const [confirmCancelOpen, setConfirmCancelOpen] = useState<boolean>(false);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState<boolean>(false);
    const history = useHistory();

    const feeLabel = formatFee(feeCents, currency, interval);
    // No recurring charge is confirmed, and no card saved for one, until the price is known.
    const feeKnown = feeCents !== null && Number.isFinite(feeCents) && feeCents > 0;
    const cancelAtPeriodEnd = Boolean(subscription?.cancelAtPeriodEnd);
    const periodEndLabel = formatDate(subscription?.currentPeriodEnd);
    const hasPaymentIssue = PAYMENT_ISSUE_STATUSES.includes(String(subscription?.status || '').toLowerCase());

    // The answer to a Greetings call was not the Greetings plan (an older or misrouted server): nothing
    // in it can be shown as the resident's Greetings state.
    const rejectOtherPlan = () => {
        clearGreetingsSummaryCache(residentId, facilityId);
        sendToast('error', "We couldn't confirm that change to Daily Greetings. Please reload the page and try again.");
    };

    // The same "paid" test the Payments page uses for the first membership payment.
    const isPaid = (sub: MembershipSubscription | null | undefined) => {
        const status = String(sub?.status || '').toLowerCase();
        return status === 'active' || status === 'trialing';
    };

    const doSubscribe = async () => {
        if (subscribeInFlightRef.current) return;
        if (!feeKnown) {
            sendToast('warn', 'The Daily Greetings price is unavailable. Please try again.');
            return;
        }
        subscribeInFlightRef.current = true;
        setActionLoading('subscribe');
        try {
            const created = await createMembershipSubscription(
                residentId,
                facilityId,
                undefined,
                'greetingsAddon',
                feeCents as number,
            );
            if (!isGreetingsAddonSubscription(created)) {
                rejectOtherPlan();
                return;
            }
            let subscription: MembershipSubscription = created;
            let approvalError = '';
            // The bank may need to approve the first payment: ask it, then re-read the subscription.
            // "Subscribed" is only said once the subscription is really paid.
            const approvalSecret = created.paymentAction?.clientSecret || '';
            if (approvalSecret && !isPaid(created)) {
                setAwaitingBank(true);
                approvalError = await approveMembershipPayment(stripePromise, approvalSecret);
                if (!approvalError) {
                    const fresh = await getMembershipSubscription(residentId, facilityId, 'greetingsAddon').catch(() => null);
                    if (fresh && isGreetingsAddonSubscription(fresh)) subscription = fresh;
                }
            }
            // The subscription now exists server-side whatever happens next: the Home tile must not draw the old answer.
            clearGreetingsSummaryCache(residentId, facilityId);
            if (isPaid(subscription)) {
                sendToast('success', 'Subscribed to Daily Greetings.');
                onChange(subscription);
            } else if (approvalError) {
                sendToast('error', `${approvalError} You are not subscribed yet. You can try again with the same or a different card.`);
            } else if (approvalSecret) {
                sendToast('warn', 'Payment approved. Daily Greetings will be on in a moment. Reload this page if it still shows sign up.');
            } else {
                sendToast('warn', 'Daily Greetings is not on yet because the first payment did not go through. Please check your card or try a different one.');
            }
        } catch (error) {
            // The call can fail after the server has already created and charged the subscription (a
            // timeout, a dropped connection). Look once more, quietly, before saying it did not work.
            const reread = await getMembershipSubscription(residentId, facilityId, 'greetingsAddon').catch(() => null);
            if (reread && isGreetingsAddonSubscription(reread) && isPaid(reread)) {
                clearGreetingsSummaryCache(residentId, facilityId);
                sendToast('success', 'Subscribed to Daily Greetings.');
                onChange(reread);
            } else {
                sendToast('error', error instanceof Error ? error.message : 'Unable to subscribe to Daily Greetings');
            }
        } finally {
            setAwaitingBank(false);
            setActionLoading('');
            subscribeInFlightRef.current = false;
        }
    };

    const handleSubscribeClick = () => {
        if (!feeKnown) {
            sendToast('warn', 'The Daily Greetings price is unavailable. Please try again.');
            return;
        }
        // Whether a card exists is unknown: look again rather than ask for a second card.
        if (cardsLookupFailed) {
            onRetryLookup?.();
            return;
        }
        if (hasDefaultPaymentMethod) {
            setConfirmSubscribeOpen(true);
        } else {
            setAddCardOpen(true);
        }
    };

    const handledSignupRequest = useRef<number>(signupRequest);
    useEffect(() => {
        if (signupRequest === handledSignupRequest.current) return;
        handledSignupRequest.current = signupRequest;
        if (mode === 'enroll') handleSubscribeClick();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signupRequest]);

    const handleCardAdded = async () => {
        setAddCardOpen(false);
        // The card is saved whatever the subscribe does: remember it, so a failed subscribe is retried
        // from the confirm step instead of asking for the same card again.
        onCardSaved?.();
        await doSubscribe();
    };

    const handleConfirmSubscribe = async () => {
        setConfirmSubscribeOpen(false);
        await doSubscribe();
    };

    const handleCancel = async () => {
        setActionLoading('cancel');
        try {
            const sub = await cancelMembershipSubscription(residentId, facilityId, 'greetingsAddon');
            if (!isGreetingsAddonSubscription(sub)) {
                rejectOtherPlan();
                return;
            }
            sendToast('success', 'Daily Greetings canceled — contact notifications have stopped.');
            onChange(sub);
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Unable to cancel Daily Greetings');
        } finally {
            setActionLoading('');
            setConfirmCancelOpen(false);
        }
    };

    const handleResume = async () => {
        setActionLoading('resume');
        try {
            const sub = await updateMembershipSubscription(residentId, facilityId, { cancelAtPeriodEnd: false }, 'greetingsAddon');
            if (!isGreetingsAddonSubscription(sub)) {
                rejectOtherPlan();
                return;
            }
            sendToast('success', 'Daily Greetings will remain active.');
            onChange(sub);
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Unable to update Daily Greetings');
        } finally {
            setActionLoading('');
        }
    };

    const feeAmount = feeLabel.split('/')[0];
    const feeInterval = feeLabel.split('/')[1] || 'month';

    const addCardModal = (
        <Modal
            open={addCardOpen}
            closeOnEscape={!savingCard}
            closeOnDimmerClick={!savingCard}
            onClose={savingCard ? undefined : () => setAddCardOpen(false)}
            size="tiny"
            className="greetings-add-card-modal"
        >
            <div className="card-header">
                <span className="card-header-icon" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1" y="4" width="22" height="16" rx="3" />
                        <line x1="1" y1="10" x2="23" y2="10" />
                    </svg>
                </span>
                <div>
                    <h2 className="card-title">Add a card</h2>
                    <div className="card-subtitle">To turn on Daily Greetings</div>
                </div>
            </div>
            <div className="card-body">
                {stripePromise ? (
                    <Elements stripe={stripePromise}>
                        <AddCardForm
                            residentId={residentId}
                            facilityId={facilityId}
                            shouldMakeDefault
                            nameLabel="Name on card"
                            submitLabel="Save & turn on"
                            footnote={
                                feeLabel ? (
                                    <p className="card-price-line">
                                        <strong>{feeAmount} a {feeInterval}.</strong> Cancel anytime from this page.
                                    </p>
                                ) : null
                            }
                            submitDisabled={!feeKnown}
                            onCancel={() => {
                                if (!savingCard) setAddCardOpen(false);
                            }}
                            onSubmittingChange={setSavingCard}
                            onSuccess={handleCardAdded}
                        />
                    </Elements>
                ) : (
                    // No publishable key: card setup cannot load, so say so and offer a retry instead of spinning forever.
                    <div className="greetings-add-card-error" role="alert">
                        <p>We couldn&apos;t load card setup right now. Please try again.</p>
                        <Button
                            primary
                            type="button"
                            onClick={() => {
                                setAddCardOpen(false);
                                onRetryLookup?.();
                            }}
                        >
                            Try again
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    );

    if (mode === 'manage' && manageSection === 'status') {
        // A healthy subscription needs no banner here: status, fee and renewal date are in
        // "View My Subscription". Only a payment problem is worth the top-of-page space.
        if (!hasPaymentIssue) return null;
        return (
            <div className="greetings-addon-card greetings-addon-card--manage greetings-addon-card--issue">
                <span className="greetings-addon-value greetings-addon-value--issue">
                    Daily Greetings payment issue — update your card
                </span>
                <Button
                    size="mini"
                    primary
                    style={{ marginLeft: 10 }}
                    onClick={() => history.push('/payments')}
                >
                    Update card
                </Button>
            </div>
        );
    }

    if (mode === 'manage' && manageSection === 'action') {
        const statusLabel = hasPaymentIssue ? 'Payment issue' : cancelAtPeriodEnd ? 'Canceling' : 'Active';
        return (
            <div className="sub-footer-row">
                <button
                    type="button"
                    className="sub-footer-link"
                    onClick={() => setSubscriptionModalOpen(true)}
                >
                    View My Subscription →
                </button>

                <Modal open={subscriptionModalOpen} onClose={() => setSubscriptionModalOpen(false)} size="mini" className="greetings-subscription-modal">
                    <Modal.Header>My Subscription</Modal.Header>
                    <Modal.Content>
                        <div className="sub-fee-row">
                            <span className="sub-fee-label">Service</span>
                            <span className="sub-fee-value">Daily Greetings</span>
                        </div>
                        <div className="sub-fee-row">
                            <span className="sub-fee-label">Status</span>
                            <span className={`sub-fee-value${hasPaymentIssue || cancelAtPeriodEnd ? ' sub-fee-value--issue' : ' sub-fee-value--active'}`}>{statusLabel}</span>
                        </div>
                        <div className="sub-fee-row">
                            <span className="sub-fee-label">Monthly Fee</span>
                            <span className="sub-fee-value sub-fee-value--fee">{feeLabel ? feeLabel.replace(/\/month$/, '/mo') : '—'}</span>
                        </div>

                        {cancelAtPeriodEnd ? (
                            <>
                                <p className="sub-action-desc">
                                    Set to cancel{periodEndLabel ? ` on ${periodEndLabel}` : ''}. Contact notifications have already stopped — you can turn them back on any time before then.
                                </p>
                                <Button
                                    basic
                                    primary
                                    fluid
                                    loading={actionLoading === 'resume'}
                                    disabled={Boolean(actionLoading)}
                                    onClick={handleResume}
                                >
                                    Keep Greetings Active
                                </Button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="cancel-sub-btn"
                                    disabled={Boolean(actionLoading)}
                                    onClick={() => {
                                        setSubscriptionModalOpen(false);
                                        setConfirmCancelOpen(true);
                                    }}
                                >
                                    Cancel Greetings Subscription
                                </button>
                            </>
                        )}
                    </Modal.Content>
                </Modal>

                <Modal open={confirmCancelOpen} onClose={() => setConfirmCancelOpen(false)} size="mini" className="greetings-confirm-modal">
                    <div className="confirm-icon" aria-hidden="true">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.3} strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                    </div>
                    <div className="confirm-title">Cancel Greetings?</div>
                    <div className="confirm-body">
                        Your contacts will no longer be notified when you press Hello!. Your check-in will still be recorded, and your greeting history will be saved.
                    </div>
                    <div className="confirm-actions">
                        <button type="button" className="confirm-btn keep" onClick={() => setConfirmCancelOpen(false)} disabled={actionLoading === 'cancel'}>
                            Keep It
                        </button>
                        <button type="button" className="confirm-btn cancel" onClick={handleCancel} disabled={actionLoading === 'cancel'}>
                            {actionLoading === 'cancel' ? <Loader active inline inverted size="tiny" /> : 'Cancel Subscription'}
                        </button>
                    </div>
                </Modal>
            </div>
        );
    }

    return (
        <div className="greetings-addon-card greetings-addon-card--enroll">
            <div className="enroll-icon">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
                </svg>
            </div>
            <div className="enroll-title">Let your family know you're doing great</div>
            <p className="enroll-body">
                One tap on Hello! already checks you in. Add Daily Greetings and that same tap sends your family a personal message — so they hear from you without you having to call.
            </p>
            <ul className="enroll-perks">
                {[
                    'A personal message sent by text and email',
                    'Choose exactly who hears from you',
                    'Write your own message, change it anytime',
                    'Cancel anytime, right from this screen',
                ].map((perk) => (
                    <li key={perk}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {perk}
                    </li>
                ))}
            </ul>
            {feeKnown ? (
                <div className="enroll-fee">Just {feeLabel}</div>
            ) : (
                <div className="enroll-fee enroll-fee--missing" role="alert">
                    Price unavailable.{' '}
                    <button type="button" className="enroll-retry-link" onClick={() => onRetryLookup?.()}>
                        Try again
                    </button>
                </div>
            )}
            <button
                type="button"
                className="enroll-signup-btn"
                disabled={Boolean(actionLoading) || !feeKnown}
                onClick={handleSubscribeClick}
            >
                {actionLoading === 'subscribe' ? (
                    <span className="enroll-signup-waiting">
                        <Loader active inline inverted size="tiny" />
                        {awaitingBank && 'Waiting for your bank'}
                    </span>
                ) : cardsLookupFailed ? (
                    "We couldn't check your cards. Try again."
                ) : (
                    'Sign Up for Greetings'
                )}
            </button>

            <Modal open={confirmSubscribeOpen} onClose={() => setConfirmSubscribeOpen(false)} size="mini">
                <Modal.Header>Sign Up for Greetings?</Modal.Header>
                <Modal.Content>
                    <p>{`You'll be charged ${feeLabel} using your card on file.`}</p>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setConfirmSubscribeOpen(false)} disabled={actionLoading === 'subscribe'}>Back</Button>
                    <Button primary loading={actionLoading === 'subscribe'} disabled={actionLoading === 'subscribe' || !feeKnown} onClick={handleConfirmSubscribe}>
                        Confirm sign up
                    </Button>
                </Modal.Actions>
            </Modal>
            {addCardModal}
        </div>
    );
};

export default GreetingsAddonCard;
